/**
 * OpenAI LLM Provider - v13 Section 6.10-6.11
 *
 * Provider implementation for OpenAI GPT models.
 * Extracted from @ownyou/iab-classifier for Sprint 2 consolidation.
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
import OpenAI from 'openai';
import { BaseLLMProvider, LLMProviderType } from './base';
import { calculateCost } from './registry';
import { configService } from '../config';
/**
 * Cache of models that require Responses API (learned at runtime)
 * When we get a "not a chat model" error, we remember to use Responses API next time
 */
const responsesApiModelsCache = new Set();
/**
 * Detect if running in Tauri environment
 */
function isTauri() {
    return typeof window !== 'undefined' &&
        window.location?.protocol === 'tauri:' &&
        !!window.__TAURI__;
}
/**
 * Get fetch function - uses Tauri's HTTP plugin when in Tauri to bypass CORS
 */
async function getTauriFetch() {
    if (!isTauri())
        return undefined;
    try {
        const { fetch: tauriFetch } = await import('@tauri-apps/plugin-http');
        console.log('[OpenAI Provider] Using Tauri HTTP plugin for CORS-free requests');
        return tauriFetch;
    }
    catch (e) {
        console.warn('[OpenAI Provider] Tauri HTTP plugin not available:', e);
        return undefined;
    }
}
/**
 * OpenAI LLM Provider
 */
export class OpenAIProvider extends BaseLLMProvider {
    constructor(config) {
        super(config);
        this.client = null;
        this.clientPromise = null;
        if (!config.apiKey) {
            throw new Error('OpenAI API key is required');
        }
        this.openaiConfig = config;
        // Model should be explicitly configured via config.model or request.model
        // Empty default will cause error if no model specified - this is intentional
        this.defaultModel = config.model ?? '';
        // Token limit should come from ConfigService.getModelMetadata() at runtime
        this.defaultMaxTokens = config.maxTokens ?? 16384;
        this.defaultTemperature = config.temperature ?? 0.7;
    }
    /**
     * Get or create the OpenAI client (lazy initialization for Tauri fetch support)
     */
    async getClient() {
        if (this.client)
            return this.client;
        // Prevent multiple initializations
        if (this.clientPromise)
            return this.clientPromise;
        this.clientPromise = (async () => {
            // Try to get Tauri's fetch for CORS-free requests
            const customFetch = await getTauriFetch();
            this.client = new OpenAI({
                apiKey: this.openaiConfig.apiKey,
                baseURL: this.openaiConfig.baseUrl,
                organization: this.openaiConfig.organization,
                dangerouslyAllowBrowser: true,
                timeout: this.openaiConfig.timeout ?? 120000,
                maxRetries: this.openaiConfig.maxRetries ?? 2,
                fetch: customFetch,
            });
            return this.client;
        })();
        return this.clientPromise;
    }
    getProviderType() {
        return LLMProviderType.OPENAI;
    }
    /**
     * Check if a model is known to require Responses API (from runtime cache)
     */
    isKnownResponsesApiModel(model) {
        // Check exact match
        if (responsesApiModelsCache.has(model))
            return true;
        // Check base model (for dated versions like gpt-5.2-pro-2025-12-11)
        for (const baseModel of responsesApiModelsCache) {
            if (model.startsWith(`${baseModel}-`) || baseModel.startsWith(`${model}-`))
                return true;
        }
        return false;
    }
    /**
     * Mark a model as requiring Responses API (learned from error)
     */
    markAsResponsesApiModel(model) {
        responsesApiModelsCache.add(model);
        this.logger.info(`[OpenAI] Learned that model ${model} requires Responses API`);
    }
    /**
     * Check if an error indicates the model requires Responses API
     */
    isNotChatModelError(error) {
        if (error instanceof OpenAI.NotFoundError || error instanceof OpenAI.BadRequestError) {
            const message = error.message?.toLowerCase() ?? '';
            return message.includes('not a chat model') ||
                message.includes('not supported in the v1/chat/completions');
        }
        return false;
    }
    /**
     * Get supported models
     * @deprecated Use configService.getModelsByProvider('openai') for dynamic model list
     */
    getSupportedModels() {
        // No hardcoded model list - use ConfigService for actual list
        return [];
    }
    async isAvailable() {
        try {
            const client = await this.getClient();
            const models = await client.models.list();
            return models.data.length > 0;
        }
        catch (error) {
            this.logger.debug(`OpenAI availability check failed: ${error}`);
            return false;
        }
    }
    /**
     * Execute request with automatic parameter detection.
     * If API returns error about max_tokens vs max_completion_tokens, retry with correct params.
     */
    async executeWithParamDetection(client, model, messages, maxTokens, temperature, isReasoning) {
        // Build params based on detected model type
        const params = {
            model,
            messages,
        };
        if (isReasoning) {
            params.max_completion_tokens = maxTokens;
            this.logger.info(`[OpenAI] Using REASONING params: max_completion_tokens=${maxTokens}, no temperature`);
        }
        else {
            params.max_tokens = maxTokens;
            params.temperature = temperature;
            this.logger.info(`[OpenAI] Using STANDARD params: max_tokens=${maxTokens}, temperature=${temperature}`);
        }
        try {
            return await client.chat.completions.create(params);
        }
        catch (error) {
            // Check if API is telling us to use different params
            if (error instanceof OpenAI.BadRequestError &&
                error.message.includes("'max_tokens'") &&
                error.message.includes("'max_completion_tokens'")) {
                this.logger.info(`[OpenAI] Model ${model} requires max_completion_tokens - retrying with REASONING params`);
                // Retry with reasoning params
                const reasoningParams = {
                    model,
                    messages,
                    max_completion_tokens: maxTokens,
                };
                return await client.chat.completions.create(reasoningParams);
            }
            // Re-throw other errors
            throw error;
        }
    }
    async complete(request) {
        const model = request.model ?? this.defaultModel;
        const maxTokens = request.maxTokens ?? this.defaultMaxTokens;
        const temperature = request.temperature ?? this.defaultTemperature;
        // If we've learned this model needs Responses API, use it directly
        if (this.isKnownResponsesApiModel(model)) {
            return this.completeWithResponsesApi(request, model);
        }
        // Validate request
        const errors = this.validateRequest(request);
        if (errors.length > 0) {
            return this.createErrorResponse(errors.join('; '), model);
        }
        // Format messages for OpenAI
        const messages = request.messages.map((msg) => ({
            role: msg.role,
            content: msg.content,
        }));
        // Get model metadata first (needed for token adjustment and isReasoning)
        const metadata = await configService.getModelMetadata(model);
        // Adjust tokens for context window
        const adjustedMaxTokens = this.adjustTokensForContext(messages, model, maxTokens, metadata);
        this.logger.info(`Using model: ${model} for OpenAI request`);
        const startTime = Date.now();
        try {
            const client = await this.getClient();
            // Determine if reasoning model (metadata already fetched above)
            const isReasoning = metadata?.isReasoningModel ?? false;
            this.logger.info(`[OpenAI] Model ${model} isReasoning=${isReasoning} (from ConfigService)`);
            // Build and execute request with automatic parameter detection
            const response = await this.executeWithParamDetection(client, model, messages, adjustedMaxTokens, temperature, isReasoning);
            const processingTime = (Date.now() - startTime) / 1000;
            // Debug logging for ALL responses to diagnose issues
            const choice = response.choices[0];
            const rawContent = choice?.message?.content;
            // ALWAYS log response details for debugging
            this.logger.info(`[OpenAI Response] Model: ${model}`, {
                finish_reason: choice?.finish_reason,
                has_content: !!rawContent,
                content_length: rawContent?.length ?? 0,
                content_preview: rawContent?.substring(0, 150) ?? '(null/undefined)',
                choices_count: response.choices?.length ?? 0,
                usage: response.usage,
            });
            // Warn if content is empty
            if (!rawContent || rawContent.length === 0) {
                this.logger.error(`[OpenAI] ⚠️ EMPTY CONTENT from model ${model}!`, {
                    full_response: JSON.stringify(response, null, 2).substring(0, 500),
                    finish_reason: choice?.finish_reason,
                    refusal: choice?.message?.refusal,
                });
            }
            const content = response.choices[0]?.message?.content ?? '';
            const inputTokens = response.usage?.prompt_tokens ?? 0;
            const outputTokens = response.usage?.completion_tokens ?? 0;
            const totalTokens = response.usage?.total_tokens ?? 0;
            const costUsd = calculateCost(model, inputTokens, outputTokens);
            this.logger.debug('Received response from OpenAI', {
                model,
                content_length: content.length,
                processing_time: processingTime,
                tokens_used: totalTokens,
                cost: costUsd,
            });
            return {
                content,
                model,
                usage: {
                    inputTokens,
                    outputTokens,
                    totalTokens,
                    costUsd,
                },
                finishReason: response.choices[0]?.finish_reason,
            };
        }
        catch (error) {
            // Check if this model requires Responses API instead of Chat Completions
            if (this.isNotChatModelError(error)) {
                this.logger.info(`[OpenAI] Model ${model} requires Responses API - retrying`);
                this.markAsResponsesApiModel(model);
                return this.completeWithResponsesApi(request, model);
            }
            return this.handleError(error, model, startTime);
        }
    }
    /**
     * Handle OpenAI-specific errors with retry logic
     */
    handleError(error, model, startTime) {
        const processingTime = (Date.now() - startTime) / 1000;
        if (error instanceof OpenAI.RateLimitError) {
            const errorMsg = `OpenAI rate limit exceeded: ${error.message}`;
            this.logger.error('OpenAI rate limit exceeded', { model, error: errorMsg, processingTime });
            return this.createErrorResponse(errorMsg, model);
        }
        if (error instanceof OpenAI.AuthenticationError) {
            const errorMsg = `OpenAI authentication failed: ${error.message}`;
            this.logger.error('OpenAI authentication failed', { error: errorMsg });
            return this.createErrorResponse(errorMsg, model);
        }
        if (error instanceof OpenAI.BadRequestError) {
            const errorMsg = `OpenAI bad request: ${error.message}`;
            this.logger.error('OpenAI bad request', { model, error: errorMsg });
            return this.createErrorResponse(errorMsg, model);
        }
        const errorMsg = `OpenAI API error: ${error instanceof Error ? error.message : String(error)}`;
        this.logger.error('OpenAI generation failed', { model, error: errorMsg, processingTime });
        return this.createErrorResponse(errorMsg, model);
    }
    /**
     * Complete using OpenAI Responses API (v1/responses)
     * Required for models like gpt-5.2-pro that don't support Chat Completions
     *
     * @see https://platform.openai.com/docs/api-reference/responses
     */
    async completeWithResponsesApi(request, model) {
        const startTime = Date.now();
        // Validate request
        const errors = this.validateRequest(request);
        if (errors.length > 0) {
            return this.createErrorResponse(errors.join('; '), model);
        }
        this.logger.info(`[OpenAI] Using Responses API for model: ${model}`);
        try {
            const client = await this.getClient();
            // Extract system instruction and user input from messages
            // Responses API uses 'instructions' for system prompt and 'input' for user messages
            let instructions;
            const inputParts = [];
            for (const msg of request.messages) {
                if (msg.role === 'system') {
                    // Concatenate multiple system messages if present
                    instructions = instructions ? `${instructions}\n\n${msg.content}` : msg.content;
                }
                else {
                    // User and assistant messages go to input
                    inputParts.push(msg.content);
                }
            }
            const input = inputParts.join('\n\n');
            // Build Responses API request
            const responsesParams = {
                model,
                input,
                ...(instructions && { instructions }),
            };
            const response = await client.responses.create(responsesParams);
            const processingTime = (Date.now() - startTime) / 1000;
            // Extract content from response
            const content = response.output_text ?? '';
            // Log response details
            this.logger.info(`[OpenAI Responses API] Model: ${model}`, {
                has_content: !!content,
                content_length: content.length,
                content_preview: content.substring(0, 150),
                usage: response.usage,
            });
            // Warn if content is empty
            if (!content || content.length === 0) {
                this.logger.error(`[OpenAI Responses API] ⚠️ EMPTY CONTENT from model ${model}!`);
            }
            // Extract usage from response
            const inputTokens = response.usage?.input_tokens ?? 0;
            const outputTokens = response.usage?.output_tokens ?? 0;
            const totalTokens = inputTokens + outputTokens;
            const costUsd = calculateCost(model, inputTokens, outputTokens);
            this.logger.debug('Received response from OpenAI Responses API', {
                model,
                content_length: content.length,
                processing_time: processingTime,
                tokens_used: totalTokens,
                cost: costUsd,
            });
            return {
                content,
                model,
                usage: {
                    inputTokens,
                    outputTokens,
                    totalTokens,
                    costUsd,
                },
                finishReason: 'stop', // Responses API doesn't have finish_reason like Chat Completions
            };
        }
        catch (error) {
            return this.handleError(error, model, startTime);
        }
    }
    /**
     * Adjust max tokens based on input size and model limits
     * Falls back to sensible defaults when metadata not available from API
     */
    adjustTokensForContext(messages, model, requestedTokens, metadata) {
        // Get limits from ConfigService metadata (not hardcoded registry stubs)
        const contextLimit = metadata?.contextWindow ?? 128000; // Default 128k
        const maxCompletion = metadata?.maxCompletionTokens ?? this.defaultMaxTokens;
        this.logger.info(`[OpenAIProvider] ${model}: context: ${contextLimit}, max_completion: ${maxCompletion} (from ConfigService)`);
        // Estimate input tokens
        const inputText = messages.map((msg) => msg.content).join('\n');
        const estimatedInputTokens = this.estimateTokens(inputText);
        // Safety margin
        const safetyMargin = 200;
        const availableTokens = contextLimit - estimatedInputTokens - safetyMargin;
        // Cap at available tokens and model's max completion
        let adjustedTokens = Math.min(requestedTokens, availableTokens, maxCompletion);
        // Ensure minimum
        if (adjustedTokens < 100) {
            this.logger.warn(`Very limited tokens available: ${adjustedTokens}`);
            adjustedTokens = Math.max(100, Math.floor(availableTokens / 2));
        }
        if (adjustedTokens !== requestedTokens) {
            this.logger.info(`Adjusted max_tokens from ${requestedTokens} to ${adjustedTokens} ` +
                `(input: ~${estimatedInputTokens}, context: ${contextLimit}, max_completion: ${maxCompletion})`);
        }
        return adjustedTokens;
    }
}
